import { useCallback } from 'react'

import { ApolloQueryResult } from 'apollo-boost'
import axios from 'axios'

import { parseFileExtension } from '@app/lib/mime'
import { requireAuthToken } from '@app/modules/Auth/resolveAuthToken'
import { useAnswerLetterRequestMutation } from '@app/queries/AnswerLetterRequest/AnswerLetterRequest.queries'
import { useAttachDocumentToLawyerMessageMutation } from '@app/queries/AttachDocumentToLawyerMessage/AttachDocumentToLawyerMessage.queries'
import { refetchPostponedLetterRequestsQuery } from '@app/queries/PostponedLetterRequests/PostponedLetterRequests.queries'
import SDK from '@app/SDK'

export type UseUploadDocumentArgs = {
  setUploadingDocument: (stats: boolean) => void
  refetchMessages: () => Promise<ApolloQueryResult<any>>
  messageId: number
  letterRequestId?: number
  refetchLetter?: () => Promise<any>
}

export const useUploadDocument = ({
  setUploadingDocument,
  refetchMessages,
  messageId,
  letterRequestId,
  refetchLetter
}: UseUploadDocumentArgs) => {
  const [attachDocumentToLawyerMessageMutation] = useAttachDocumentToLawyerMessageMutation()
  const [answerLetterRequest] = useAnswerLetterRequestMutation()

  return useCallback(async (file: File[]) => {
    setUploadingDocument(true)
    const token = await requireAuthToken()
    const { documentId, signedUrl, headers } = await SDK.documentAnalyzer.authorizeLawyerUpload({
      token,
      file: {
        fileExtension: parseFileExtension(file[0].name, file[0].type),
        fileName: file[0].name
      }
    })

    await axios.put(signedUrl, file[0], {
      headers: {
        'Content-Type': file[0].type,
        ...headers
      }
    })
    await attachDocumentToLawyerMessageMutation({ variables: { documentId, messageId } })
    if (letterRequestId) {
      await answerLetterRequest({
        variables: { letterRequestId, lawyerLetterId: documentId },
        refetchQueries: [refetchPostponedLetterRequestsQuery()]
      })
      typeof refetchLetter === 'function' && (await refetchLetter())
    }
    await refetchMessages()
    setUploadingDocument(false)
  }, [])
}
